import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, LockSimple, X } from "@phosphor-icons/react";
import { portalApi, portalMoney } from "@/lib/portalApi";

const digits = (v) => v.replace(/\D/g, "");
const groupCard = (v) => digits(v).slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ");
const groupExp = (v) => {
  const d = digits(v).slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

// Card form for Paya Connect "direct" mode: fields post once to our backend, which runs the
// sale and never stores the number.
export const PayaCardForm = ({ invoiceId, amount, sandbox, onPaid, onClose }) => {
  const [f, setF] = useState({ name: "", card: "", exp: "", cvv: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (digits(f.card).length < 12 || digits(f.exp).length !== 4) {
      toast.error("Check the card number and expiry");
      return;
    }
    setBusy(true);
    try {
      const { data } = await portalApi.post(`/invoices/${invoiceId}/paya/charge`, {
        card_number: digits(f.card),
        exp_date: digits(f.exp),
        cvv: f.cvv,
        zip_code: f.zip,
        name: f.name,
      });
      toast.success(`Payment approved${data.last_four ? ` · card •••• ${data.last_four}` : ""}`);
      onPaid?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The card was not approved");
    }
    setBusy(false);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="paya-card-form"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_12px_40px_-16px_rgba(15,23,42,0.35)]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <CreditCard size={18} weight="bold" className="text-[#c39e6d]" />
          Pay {portalMoney(amount)} by card
          {sandbox && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
              test mode
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label="Close"
          data-testid="paya-card-close"
          onClick={onClose}
          className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Name on card
          </span>
          <input
            data-testid="paya-card-name"
            value={f.name}
            onChange={set("name")}
            autoComplete="cc-name"
            className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-[#c39e6d]"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Card number
          </span>
          <input
            data-testid="paya-card-number"
            value={f.card}
            onChange={(e) => setF({ ...f, card: groupCard(e.target.value) })}
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="4111 1111 1111 1111"
            className="font-mono-data mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-[#c39e6d]"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Expiry (MM/YY)
          </span>
          <input
            data-testid="paya-card-exp"
            value={f.exp}
            onChange={(e) => setF({ ...f, exp: groupExp(e.target.value) })}
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM/YY"
            className="font-mono-data mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-[#c39e6d]"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              CVV
            </span>
            <input
              data-testid="paya-card-cvv"
              value={f.cvv}
              onChange={(e) => setF({ ...f, cvv: digits(e.target.value).slice(0, 4) })}
              inputMode="numeric"
              autoComplete="cc-csc"
              className="font-mono-data mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-[#c39e6d]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              ZIP
            </span>
            <input
              data-testid="paya-card-zip"
              value={f.zip}
              onChange={set("zip")}
              inputMode="numeric"
              autoComplete="postal-code"
              className="font-mono-data mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-[#c39e6d]"
            />
          </label>
        </div>
      </div>
      <button
        type="submit"
        data-testid="paya-card-submit"
        disabled={busy}
        className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#c39e6d] text-sm font-semibold text-white transition-colors hover:bg-[#a9855a] disabled:opacity-60"
      >
        <LockSimple size={16} weight="bold" />
        {busy ? "Charging…" : `Pay ${portalMoney(amount)}`}
      </button>
      <p className="mt-2 text-center text-[11px] text-slate-400">
        Processed securely by Paya Connect. We never store your card number.
      </p>
    </form>
  );
};
